import dayjs from 'dayjs';
import html3pdf from 'html3pdf';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { Descendant } from 'slate';

import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { fetchImageFromS3AndConvertToBase64 } from '../../../utils/s3';
import { regenerateIcon } from '../../../assets/hostedassets';
import { TIME_DATE_FORMAT } from '../../../utils/constants/commonConstants';
import { closeCommentsThread, resetAndHideCommentsThread } from '../../../store/commentsThread/commentsThread_actions';
import { convertToSnakeCase, getSubdomain } from '../../../utils/utils';
import { Mixpanel } from '../../../utils/mixpanel';

import { Button, Modal } from '../../_custom';
import LoadingSpinner from '../../_custom/LoadingSpinner/LoadingSpinner';
import TextEditor from '../../TextEditor/TextEditor';

import styles from './EngagementLetterEditor.module.scss';
import { AICA_ENGAGEMENT_LETTER_PREVIEW, AICA_ENGAGEMENT_LETTER_SAVE } from '../../../utils/constants/mixpanelEvents/investorEvents';
import { TEXT_EDITOR_TYPES } from '../../../store/investor/constants';
import { slateToHtml } from '../../TextEditor/utils/SlateSerialisationUtil';
import { getPdfExportStyles, getDefaultPdfOptions } from '../../TextEditor/utils/pdfExportStyles';

function EngagementLetterEditor({
    source,
    openModal,
    orgId,
    orgName,
    generating,
    editorInitialData,
    editorReady,
    savingData,
    saveEngagementLetter,
    setShowRegenConfirmModal,
    userInfo,
}) {
    const dispatch = useAppDispatch();
    const [saving, setSaving] = useState(false);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const contentToPrint = useRef(null);
    const editorContent = useRef<Descendant[]>([]);
    const domainMetadata = useAppSelector((state) => state.user.domainMetadata);
    const mainImageUrl = domainMetadata?.mainImageUrl;
    const [triggerSave, setTriggerSave] = useState(false);
    const user = useAppSelector((state) => state.user.userData);

    useEffect(() => {
        dispatch(resetAndHideCommentsThread());
    }, []);

    useEffect(() => {
        if (triggerSave) {
            setTriggerSave(false);
        }
    }, [triggerSave, editorContent, orgId]);

    /**
     * Generates a standalone HTML element from Slate JSON content for PDF export.
     * This approach avoids DOM cloning issues and ensures CSS is properly embedded.
     */
    const generateHTMLFromSlateContent = async () => {
        try {
            // Clean trailing empty paragraphs before conversion to prevent extra page
            const cleanedContent = editorContent.current.filter((node: any, index: number, arr: any[]) => {
                // Keep all nodes except trailing empty paragraphs
                if (index === arr.length - 1) {
                    return !(node.type === 'paragraph' && node.children?.length === 1 && node.children[0]?.text === '');
                }
                return true;
            });

            // Generate HTML content from cleaned Slate JSON
            const htmlContent = slateToHtml(cleanedContent.length > 0 ? cleanedContent : editorContent.current);

            // Create a container element with embedded styles
            const container = document.createElement('div');
            // Use inline-block to wrap content tightly (prevents extra page)
            container.style.display = 'inline-block';
            container.style.width = '100%';
            container.style.margin = '0';
            container.style.padding = '0';

            // Use shared PDF export styles from utility
            // Added CSS to handle trailing br elements that could cause extra pages
            container.innerHTML = `
                <style>
                    ${getPdfExportStyles()}
                    /* Prevent trailing br from adding extra space */
                    br:last-child { display: none; }
                    .content > br:last-child { display: none; }
                    .content { margin: 0 !important; padding: 0 !important; }
                    .content > *:last-child { margin-bottom: 0 !important; }
                </style>
                <div class="content">${htmlContent}</div>
            `;

            // Add logo if available
            if (mainImageUrl) {
                const response: any = await fetchImageFromS3AndConvertToBase64(mainImageUrl);
                if (response) {
                    const topDiv = document.createElement('div');
                    topDiv.style.display = 'flex';
                    topDiv.style.justifyContent = 'flex-start';
                    topDiv.style.marginBottom = '16px';

                    const topImg = document.createElement('img');
                    topImg.src = response;
                    topImg.alt = '';
                    topImg.style.height = '30px';
                    topImg.crossOrigin = 'anonymous';
                    topDiv.appendChild(topImg);

                    container.insertBefore(topDiv, container.firstChild);
                }
            }

            return container;
        } catch (err) {
            console.error('Error generating HTML from Slate content', err);
            // Fallback to DOM cloning if JSON conversion fails
            return getFallbackHTMLElement();
        }
    };

    /**
     * Fallback: Clone DOM element (original approach) if JSON conversion fails.
     */
    const getFallbackHTMLElement = async () => {
        const el = document?.getElementById('element-to-be-downloaded-as-pdf');
        const clonedElement = el?.cloneNode(true) as HTMLElement;

        if (mainImageUrl && clonedElement) {
            const response: any = await fetchImageFromS3AndConvertToBase64(mainImageUrl);
            if (response) {
                const topDiv = document.createElement('div');
                topDiv.style.display = 'flex';
                topDiv.style.justifyContent = 'flex-start';
                topDiv.style.marginBottom = '16px';

                const topImg = document.createElement('img');
                topImg.src = response;
                topImg.alt = '';
                topImg.style.height = '30px';
                topImg.crossOrigin = 'anonymous';
                topDiv.appendChild(topImg);

                clonedElement.insertBefore(topDiv, clonedElement.firstChild);
            }
        }
        return clonedElement;
    };

    const removePageBreaks = () => {
        const elements = document.querySelectorAll('#page-break-line');
        elements.forEach((hr, index) => {
            const hrElement = hr as HTMLElement;
            hrElement.style.visibility = 'hidden';
        });
    };

    const showPageBreaks = () => {
        const elements = document.querySelectorAll('#page-break-line');
        elements.forEach((hr, index) => {
            const hrElement = hr as HTMLElement;
            hrElement.style.visibility = 'visible';
        });
    };

    const handlePreview = useReactToPrint({
        onPrintError: (error) => console.error(error),
        content: () => contentToPrint.current,
        documentTitle: 'Print This Document',
        removeAfterPrint: true,
        print: async (printIframe: HTMLIFrameElement) => {
            removePageBreaks();
            const getElementForExport = await generateHTMLFromSlateContent();

            try {
                // setLoadingPreview(true);
                const document = printIframe.contentDocument;
                if (document) {
                    // Use shared PDF export options
                    const opt = getDefaultPdfOptions(`EL_${convertToSnakeCase(orgName)}.pdf`);
                    html3pdf()
                        .from(getElementForExport)
                        .set(opt)
                        .toPdf()
                        .output('bloburl')
                        .then((bloburl) => {
                            window.open(bloburl, '_blank');
                            setLoadingPreview(false);
                        })
                        .catch(() => setLoadingPreview(false));
                }
            } catch (err) {
                console.error('Error in generating preview', err);
            } finally {
                showPageBreaks();
            }
        },
    });

    const handleClose = () => {
        dispatch(closeCommentsThread());
        setTriggerSave(true);
        saveEngagementLetter(editorContent.current);
    };

    const userMessage = useMemo(() => {
        if (!userInfo?.userName || !userInfo?.onePagerData?.lastUpdatedAt) {
            return '';
        }
        const formattedDate = dayjs(userInfo.onePagerData.lastUpdatedAt).format(TIME_DATE_FORMAT);

        return `Last edited on ${formattedDate} by ${userInfo.userName}`;
    }, [userInfo]);

    const handlePreviewClick = (e) => {
        e.preventDefault();
        setLoadingPreview(true);
        handlePreview(null, () => contentToPrint?.current);

        Mixpanel.track(AICA_ENGAGEMENT_LETTER_PREVIEW, {
            userRole: user?.accountType,
            pnoPlatform: getSubdomain(),
            userId: user?.userId,
            currentUrl: window.location.href,
            organizationId: orgId,
        });
    };

    const handleSaveAndClose = (e) => {
        e.preventDefault();
        setSaving(true);
        setTriggerSave(true);
        dispatch(closeCommentsThread());
        saveEngagementLetter(editorContent.current);
        downloadPDF(orgName);

        Mixpanel.track(AICA_ENGAGEMENT_LETTER_SAVE, {
            userRole: user?.accountType,
            pnoPlatform: getSubdomain(),
            userId: user?.userId,
            currentUrl: window.location.href,
            organizationId: orgId,
        });
    };

    const downloadPDF = async (companyName) => {
        removePageBreaks();
        const getElementForExport = await generateHTMLFromSlateContent();

        try {
            const currentOrgName = companyName || orgName || 'Company';
            const finalFileName = currentOrgName.replace(/[^a-zA-Z0-9]/g, '_');

            // Use shared PDF export options
            const opt = getDefaultPdfOptions(`Engagement_Letter_${finalFileName}.pdf`);

            // Use save method for actual download with correct filename
            await html3pdf().from(getElementForExport).set(opt).save(`Engagement_Letter_${finalFileName}.pdf`);

            setSaving(false);
        } catch (err) {
            console.error('Error in generating PDF', err);
            setSaving(false);
        } finally {
            showPageBreaks();
        }
    };

    // Add loading state UI for regeneration
    const renderRegenerateButton = () => {
        return (
            <div
                className={styles.Regen}
                onClick={() => {
                    !generating && setShowRegenConfirmModal(true);
                }}
            >
                {generating ? (
                    <>
                        <LoadingSpinner color="var(--primary-text-colour)" height="18px" style={{ margin: 0 }} />
                        <span>Regenerating</span>
                    </>
                ) : (
                    <>
                        <img src={regenerateIcon} alt={'re'} height={'24px'} />
                        <span>Regenerate</span>
                    </>
                )}
            </div>
        );
    };

    return openModal ? (
        <Modal
            size="full"
            open={openModal}
            style={{ padding: 0, fontSize: '12px', lineHeight: '140%', position: 'absolute' }}
            persistent
            withoutCloseButton
        >
            <div className={styles.Container}>
                <div className={styles.Top}>
                    {!!editorReady && (
                        <TextEditor
                            source={source}
                            editorInitialData={editorInitialData}
                            targetRef={contentToPrint}
                            editorContentRef={editorContent}
                            assesseeOrgId={orgId}
                            generating={generating}
                            onClose={handleClose}
                            savingData={savingData}
                            editorType={TEXT_EDITOR_TYPES.ENGAGEMENT_LETTER}
                        />
                    )}
                    <div className={styles.Footer}>
                        <div>{renderRegenerateButton()}</div>

                        <div className={styles.Text}>*Right-click tables to modify: add rows, add columns, or delete</div>

                        {userMessage && <div className={styles.Text}>{userMessage}</div>}

                        <div className={styles.Buttons}>
                            <Button
                                text="Preview"
                                variant="purpleinverted"
                                onClick={handlePreviewClick}
                                style={{ margin: '0 20px 0 0 ' }}
                                loading={loadingPreview}
                            />
                            <Button
                                text="Save & Download"
                                variant="primary"
                                onClick={handleSaveAndClose}
                                style={{ marginTop: '0' }}
                                loading={saving}
                            />
                        </div>
                    </div>
                </div>

                <div className={styles.Comments} id="el-comment-container">
                    {/* //Container Element Where comments thread is rendered via portal */}
                </div>
            </div>
        </Modal>
    ) : (
        <></>
    );
}

export default EngagementLetterEditor;
